import { useState, useRef, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Upload,
  Square,
  Type,
  Download,
  Trash2,
  Palette,
  X,
  Image as ImageIcon,
  RotateCcw,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Flyer Rebrander — an in-app tool for taking a supplier's marketing image
 * (typically JPG/PNG they email you), covering their price and their
 * "Only available at X" ribbon, adding your own price + text, and exporting
 * the finished flyer as PNG with Bosveld's contact-strip footer baked in.
 *
 * Everything happens client-side — no upload, no server round-trip, image
 * never leaves the browser.
 */
export default function FlyerRebrander() {
  const [image, setImage] = useState(null); // { src, width, height }
  const [displayScale, setDisplayScale] = useState(1); // canvas-preview scale factor
  const [overlays, setOverlays] = useState([]); // {id,type,x,y,w,h,fill,text,color,font,size,align}
  const [selectedId, setSelectedId] = useState(null);
  const [companyFooter, setCompanyFooter] = useState({
    name: "BOSVELD BOU & HARDEWARE (PTY) LTD",
    phone: "",
    email: "",
    address: "",
  });
  const [includeFooter, setIncludeFooter] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const previewRef = useRef(null);
  const fileInputRef = useRef(null);
  const draggingRef = useRef(null); // {id, mode:'move'|'resize', startX, startY, startBox}

  // Fetch Bosveld company details to auto-populate the footer band.
  useEffect(() => {
    axios
      .get(`${API}/api/settings/company`)
      .then((res) => {
        const s = res.data || {};
        setCompanyFooter({
          name: (s.company_name || "BOSVELD BOU & HARDEWARE (PTY) LTD").toUpperCase(),
          phone: s.phone || "",
          email: s.email || "",
          address: [s.address_line1, s.address_line2, s.city, s.postal_code]
            .filter(Boolean)
            .join(", "),
        });
      })
      .catch(() => {/* silent — keep defaults */});
  }, []);

  // ---------------- Image load ----------------

  const handleUpload = (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please upload a PNG or JPG image.");
      return;
    }
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new window.Image();
      img.onload = () => {
        setImage({ src: e.target.result, width: img.naturalWidth, height: img.naturalHeight });
        setOverlays([]);
        setSelectedId(null);
      };
      img.onerror = () => toast.error("Could not read that image file.");
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  };

  // Compute display scale (fits the flyer to 900px width max).
  useEffect(() => {
    if (!image || !previewRef.current) return;
    const container = previewRef.current;
    const maxW = Math.min(900, container.clientWidth - 20);
    const scale = Math.min(1, maxW / image.width);
    setDisplayScale(scale);
  }, [image]);

  // Global Ctrl/Cmd+V paste handler — accepts an image straight from the OS
  // clipboard (e.g. after a screenshot or a right-click "Copy image" on a
  // supplier's website). Skips paste when the user is typing in an input so
  // it doesn't hijack normal text pasting.
  useEffect(() => {
    const onPaste = (e) => {
      const target = e.target;
      const tag = target?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || target?.isContentEditable) return;
      const items = e.clipboardData?.items;
      if (!items) return;
      for (const item of items) {
        if (item.kind === "file" && item.type.startsWith("image/")) {
          const file = item.getAsFile();
          if (file) {
            e.preventDefault();
            handleUpload(file);
            toast.success("Image pasted from clipboard");
            return;
          }
        }
      }
    };
    window.addEventListener("paste", onPaste);
    return () => window.removeEventListener("paste", onPaste);
  }, []);

  // ---------------- Overlay helpers ----------------

  const addCover = () => {
    if (!image) return;
    const id = `ov-${Date.now()}`;
    // Default: a white rectangle 30% × 8% dropped in the top-right area.
    setOverlays((o) => [
      ...o,
      {
        id,
        type: "cover",
        x: Math.round(image.width * 0.55),
        y: Math.round(image.height * 0.05),
        w: Math.round(image.width * 0.35),
        h: Math.round(image.height * 0.08),
        fill: "#FFFFFF",
        text: "",
        color: "#111111",
        size: Math.max(24, Math.round(image.height * 0.03)),
        align: "center",
      },
    ]);
    setSelectedId(id);
  };

  const addText = () => {
    if (!image) return;
    const id = `ov-${Date.now()}`;
    setOverlays((o) => [
      ...o,
      {
        id,
        type: "text",
        x: Math.round(image.width * 0.1),
        y: Math.round(image.height * 0.6),
        w: Math.round(image.width * 0.4),
        h: Math.round(image.height * 0.08),
        fill: "transparent",
        text: "YOUR PRICE",
        color: "#000000",
        size: Math.max(28, Math.round(image.height * 0.035)),
        align: "left",
      },
    ]);
    setSelectedId(id);
  };

  const updateOverlay = (id, patch) => {
    setOverlays((o) => o.map((ov) => (ov.id === id ? { ...ov, ...patch } : ov)));
  };

  const removeOverlay = (id) => {
    setOverlays((o) => o.filter((ov) => ov.id !== id));
    if (selectedId === id) setSelectedId(null);
  };

  // ---------------- Drag handlers ----------------

  const onMouseDownOverlay = (e, ov, mode) => {
    e.stopPropagation();
    setSelectedId(ov.id);
    draggingRef.current = {
      id: ov.id,
      mode,
      startX: e.clientX,
      startY: e.clientY,
      startBox: { x: ov.x, y: ov.y, w: ov.w, h: ov.h },
    };
    window.addEventListener("mousemove", onMouseMoveWindow);
    window.addEventListener("mouseup", onMouseUpWindow);
  };

  const onMouseMoveWindow = (e) => {
    const d = draggingRef.current;
    if (!d || !image) return;
    const dx = (e.clientX - d.startX) / displayScale;
    const dy = (e.clientY - d.startY) / displayScale;
    setOverlays((o) =>
      o.map((ov) => {
        if (ov.id !== d.id) return ov;
        if (d.mode === "move") {
          return {
            ...ov,
            x: Math.max(0, Math.min(image.width - ov.w, d.startBox.x + dx)),
            y: Math.max(0, Math.min(image.height - ov.h, d.startBox.y + dy)),
          };
        }
        // resize
        return {
          ...ov,
          w: Math.max(20, Math.min(image.width - ov.x, d.startBox.w + dx)),
          h: Math.max(20, Math.min(image.height - ov.y, d.startBox.h + dy)),
        };
      }),
    );
  };

  const onMouseUpWindow = () => {
    draggingRef.current = null;
    window.removeEventListener("mousemove", onMouseMoveWindow);
    window.removeEventListener("mouseup", onMouseUpWindow);
  };

  // ---------------- Export ----------------

  const downloadPng = async () => {
    if (!image) return;
    setDownloading(true);
    try {
      const canvas = document.createElement("canvas");
      const footerH = includeFooter ? Math.round(image.height * 0.09) : 0;
      canvas.width = image.width;
      canvas.height = image.height + footerH;
      const ctx = canvas.getContext("2d");
      // background so any transparent bits are safe
      ctx.fillStyle = "#FFFFFF";
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // Draw the source image
      const img = await new Promise((resolve, reject) => {
        const i = new window.Image();
        i.onload = () => resolve(i);
        i.onerror = reject;
        i.src = image.src;
      });
      ctx.drawImage(img, 0, 0, image.width, image.height);

      // Draw each overlay
      for (const ov of overlays) {
        if (ov.fill && ov.fill !== "transparent") {
          ctx.fillStyle = ov.fill;
          ctx.fillRect(ov.x, ov.y, ov.w, ov.h);
        }
        if (ov.text && ov.text.trim()) {
          ctx.fillStyle = ov.color;
          ctx.font = `bold ${ov.size}px "Segoe UI", Arial, sans-serif`;
          ctx.textBaseline = "middle";
          const lines = ov.text.split(/\n/);
          const lineH = ov.size * 1.15;
          const totalH = lines.length * lineH;
          let y = ov.y + ov.h / 2 - totalH / 2 + lineH / 2;
          for (const line of lines) {
            let x = ov.x + 6;
            ctx.textAlign = "left";
            if (ov.align === "center") {
              x = ov.x + ov.w / 2;
              ctx.textAlign = "center";
            } else if (ov.align === "right") {
              x = ov.x + ov.w - 6;
              ctx.textAlign = "right";
            }
            ctx.fillText(line, x, y);
            y += lineH;
          }
        }
      }

      // Draw Bosveld footer band
      if (includeFooter) {
        const y0 = image.height;
        ctx.fillStyle = "#000000";
        ctx.fillRect(0, y0, image.width, footerH);
        ctx.fillStyle = "#FFFFFF";
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        const lines = [companyFooter.name, companyFooter.phone, companyFooter.email, companyFooter.address]
          .filter((l) => l && l.trim())
          .slice(0, 4);
        const lineH = footerH / (lines.length + 1);
        lines.forEach((l, idx) => {
          const isTitle = idx === 0;
          ctx.font = `${isTitle ? "bold" : "normal"} ${isTitle ? Math.round(footerH * 0.22) : Math.round(footerH * 0.16)}px "Segoe UI", Arial, sans-serif`;
          ctx.fillText(l, image.width / 2, y0 + lineH * (idx + 1));
        });
      }

      const blob = await new Promise((r) => canvas.toBlob(r, "image/png"));
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      const stamp = new Date().toISOString().slice(0, 10);
      link.href = url;
      link.download = `bosveld-flyer-${stamp}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      toast.success("Flyer downloaded.");
    } catch (err) {
      console.error(err);
      toast.error("Failed to render flyer.");
    } finally {
      setDownloading(false);
    }
  };

  // ---------------- Derived state ----------------

  const selected = overlays.find((o) => o.id === selectedId) || null;

  // ---------------- Render ----------------

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-4" data-testid="flyer-rebrander-page">
      <div className="flex items-start justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
            <ImageIcon className="h-8 w-8 text-indigo-600" />
            Flyer Rebrander
          </h1>
          <p className="text-gray-600 text-sm mt-1">
            Upload any supplier flyer image, cover their prices &amp; branding, add your own text, and download the finished flyer with Bosveld&apos;s contact strip baked in. Everything stays in your browser — the image is never uploaded to any server.
          </p>
        </div>
      </div>

      {/* Toolbar */}
      <Card>
        <CardContent className="pt-4 flex flex-wrap gap-2 items-center">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/png,image/jpeg,image/webp"
            style={{ display: "none" }}
            onChange={(e) => handleUpload(e.target.files?.[0])}
          />
          <Button variant="outline" onClick={() => fileInputRef.current?.click()} data-testid="flyer-upload-btn">
            <Upload className="h-4 w-4 mr-1" /> Upload flyer
          </Button>
          <Button variant="outline" disabled={!image} onClick={addCover} data-testid="flyer-add-cover">
            <Square className="h-4 w-4 mr-1" /> Add cover box
          </Button>
          <Button variant="outline" disabled={!image} onClick={addText} data-testid="flyer-add-text">
            <Type className="h-4 w-4 mr-1" /> Add text
          </Button>
          {image && (
            <Button
              variant="outline"
              className="text-red-700"
              onClick={() => { setImage(null); setOverlays([]); setSelectedId(null); }}
              data-testid="flyer-reset"
            >
              <RotateCcw className="h-4 w-4 mr-1" /> Reset
            </Button>
          )}
          <label className="flex items-center gap-1.5 text-xs ml-4 cursor-pointer">
            <input
              type="checkbox"
              checked={includeFooter}
              onChange={(e) => setIncludeFooter(e.target.checked)}
              data-testid="flyer-footer-toggle"
            />
            Add Bosveld contact-strip footer
          </label>
          <div className="flex-1" />
          <Button
            className="bg-indigo-600 hover:bg-indigo-700"
            disabled={!image || downloading}
            onClick={downloadPng}
            data-testid="flyer-download"
          >
            <Download className="h-4 w-4 mr-1" />
            {downloading ? "Rendering…" : "Download PNG"}
          </Button>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {/* Preview canvas */}
        <div className="md:col-span-3">
          <Card>
            <CardContent className="pt-4" ref={previewRef}>
              {!image ? (
                <div className="border-2 border-dashed border-gray-300 rounded-lg py-24 text-center text-gray-500">
                  <ImageIcon className="mx-auto h-12 w-12 text-gray-400 mb-3" />
                  <p>Click &quot;Upload flyer&quot; to get started.</p>
                  <p className="text-xs mt-2">Tip: PNG / JPG at any resolution. Bigger = sharper printout.</p>
                  <p className="text-xs mt-1 text-indigo-600">Or press <kbd className="px-1.5 py-0.5 rounded border border-indigo-300 bg-indigo-50 font-mono text-[11px]">Ctrl</kbd> + <kbd className="px-1.5 py-0.5 rounded border border-indigo-300 bg-indigo-50 font-mono text-[11px]">V</kbd> to paste an image from your clipboard.</p>
                </div>
              ) : (
                <div
                  className="relative select-none mx-auto shadow-lg"
                  style={{
                    width: image.width * displayScale,
                    height: image.height * displayScale,
                    background: `#fff url(${image.src}) center / contain no-repeat`,
                  }}
                  onMouseDown={() => setSelectedId(null)}
                >
                  {overlays.map((ov) => (
                    <div
                      key={ov.id}
                      className={`absolute border ${selectedId === ov.id ? "border-indigo-600 outline outline-2 outline-indigo-300" : "border-dashed border-gray-500/70"}`}
                      style={{
                        left: ov.x * displayScale,
                        top: ov.y * displayScale,
                        width: ov.w * displayScale,
                        height: ov.h * displayScale,
                        background: ov.fill === "transparent" ? "transparent" : ov.fill,
                        cursor: draggingRef.current?.id === ov.id ? "grabbing" : "grab",
                      }}
                      onMouseDown={(e) => onMouseDownOverlay(e, ov, "move")}
                      data-testid={`flyer-overlay-${ov.id}`}
                    >
                      {ov.text && (
                        <div
                          className="w-full h-full flex items-center justify-center px-1 pointer-events-none whitespace-pre-line leading-tight"
                          style={{
                            color: ov.color,
                            fontWeight: 700,
                            fontSize: ov.size * displayScale,
                            textAlign: ov.align,
                            justifyContent: ov.align === "left" ? "flex-start" : ov.align === "right" ? "flex-end" : "center",
                          }}
                        >
                          {ov.text}
                        </div>
                      )}
                      {/* Resize handle */}
                      <div
                        className="absolute -bottom-1 -right-1 w-3 h-3 bg-indigo-600 border border-white cursor-nwse-resize"
                        onMouseDown={(e) => onMouseDownOverlay(e, ov, "resize")}
                      />
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Overlay editor sidebar */}
        <div className="md:col-span-1">
          <Card>
            <CardContent className="pt-4 space-y-3">
              <div className="text-sm font-semibold text-gray-800 flex items-center gap-1.5">
                <Palette className="h-4 w-4" /> Overlays ({overlays.length})
              </div>
              {overlays.length === 0 && (
                <p className="text-xs text-gray-500">
                  Click &quot;Add cover box&quot; to hide the supplier&apos;s price / branding, then &quot;Add text&quot; to put your own info on top.
                </p>
              )}
              <div className="space-y-1 max-h-40 overflow-auto">
                {overlays.map((ov, idx) => (
                  <div
                    key={ov.id}
                    className={`flex items-center justify-between px-2 py-1 rounded text-xs cursor-pointer ${selectedId === ov.id ? "bg-indigo-50 border border-indigo-300" : "hover:bg-gray-50"}`}
                    onClick={() => setSelectedId(ov.id)}
                  >
                    <span className="truncate">
                      {ov.type === "cover" ? "▭ Cover" : "T Text"} {idx + 1}
                      {ov.text ? ` — ${ov.text.split("\n")[0].slice(0, 15)}` : ""}
                    </span>
                    <button
                      className="text-red-600 hover:bg-red-50 rounded p-0.5"
                      onClick={(e) => { e.stopPropagation(); removeOverlay(ov.id); }}
                      title="Delete"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ))}
              </div>

              {/* Selected overlay editor */}
              {selected && (
                <div className="border-t pt-3 space-y-2">
                  <div className="text-xs font-semibold text-gray-700 flex items-center justify-between">
                    Edit selected
                    <button onClick={() => setSelectedId(null)} className="text-gray-500 hover:text-gray-700">
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </div>
                  <div>
                    <Label className="text-xs">Text (blank = plain cover box)</Label>
                    <Textarea
                      rows={2}
                      value={selected.text}
                      onChange={(e) => updateOverlay(selected.id, { text: e.target.value })}
                      className="text-xs"
                      data-testid="flyer-selected-text"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-xs">Fill</Label>
                      <Input
                        type="color"
                        value={selected.fill === "transparent" ? "#ffffff" : selected.fill}
                        onChange={(e) => updateOverlay(selected.id, { fill: e.target.value })}
                        className="h-8 p-1"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Text colour</Label>
                      <Input
                        type="color"
                        value={selected.color}
                        onChange={(e) => updateOverlay(selected.id, { color: e.target.value })}
                        className="h-8 p-1"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-xs">Font size (px)</Label>
                      <Input
                        type="number"
                        min={8}
                        max={400}
                        value={selected.size}
                        onChange={(e) => updateOverlay(selected.id, { size: Math.max(8, Number(e.target.value) || 24) })}
                        className="h-8"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Align</Label>
                      <select
                        value={selected.align}
                        onChange={(e) => updateOverlay(selected.id, { align: e.target.value })}
                        className="h-8 w-full border rounded px-2 text-sm"
                      >
                        <option value="left">Left</option>
                        <option value="center">Centre</option>
                        <option value="right">Right</option>
                      </select>
                    </div>
                  </div>
                  <button
                    onClick={() => updateOverlay(selected.id, { fill: selected.fill === "transparent" ? "#ffffff" : "transparent" })}
                    className="text-xs text-indigo-600 hover:underline"
                  >
                    Toggle background {selected.fill === "transparent" ? "on" : "off (text-only)"}
                  </button>
                </div>
              )}

              {/* Footer preview */}
              {includeFooter && (
                <div className="border-t pt-3 space-y-1 text-xs text-gray-600">
                  <div className="font-semibold text-gray-800">Footer will read:</div>
                  <div className="bg-black text-white rounded px-2 py-1.5 text-center text-[10px] leading-tight">
                    <div className="font-bold">{companyFooter.name}</div>
                    {companyFooter.phone && <div>{companyFooter.phone}</div>}
                    {companyFooter.email && <div>{companyFooter.email}</div>}
                    {companyFooter.address && <div>{companyFooter.address}</div>}
                  </div>
                  <p className="text-[10px] text-gray-500">Pulled live from Company Settings.</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
